import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X,
    Minus,
    Plus,
    ShoppingBag,
    Trash2,
    Truck,
    ShieldCheck,
    CreditCard,
    Gift,
    Zap,
    CheckCircle2,
    PartyPopper
} from 'lucide-react';
import { useCartStore, useCartTotals } from '../../store/cartStore';
import { formatPrice, getImagePlaceholder } from '../../lib/utils';
import { settingsApi } from '../../lib/api';

export function CartSidebar() {
    const navigate = useNavigate();

    const isOpen = useCartStore((state) => state.isOpen);
    const items = useCartStore((state) => state.items);
    const closeCart = useCartStore((state) => state.closeCart);
    const removeItem = useCartStore((state) => state.removeItem);
    const updateQuantity = useCartStore((state) => state.updateQuantity);

    const { totalItems, totalPrice } = useCartTotals();

    // Fetch store settings
    const { data: settings } = useQuery({
        queryKey: ['store-settings'],
        queryFn: settingsApi.get,
        staleTime: 1000 * 60 * 10, // 10 minutes
    });

    // Use settings or defaults
    const freeShippingEnabled = settings?.freeShippingEnabled ?? true;
    const freeShippingThreshold = settings?.freeShippingThreshold ?? 5000;
    const codEnabled = settings?.codEnabled ?? true;
    const codLabel = settings?.codLabel ?? 'Paiement à la Livraison';

    // Calculate free shipping progress
    const freeShippingProgress = freeShippingEnabled 
        ? Math.min((totalPrice / freeShippingThreshold) * 100, 100) 
        : 0;
    const amountToFreeShipping = freeShippingEnabled 
        ? Math.max(freeShippingThreshold - totalPrice, 0) 
        : 0;
    const hasFreeShipping = freeShippingEnabled && totalPrice >= freeShippingThreshold;

    const handleCheckout = () => {
        closeCart();
        navigate('/checkout');
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeCart}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
                    />

                    {/* Sidebar - Full screen on mobile, max-w-md on desktop */}
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                        className="fixed top-0 right-0 bottom-0 w-full sm:max-w-md bg-gray-50 z-50 shadow-2xl flex flex-col safe-area-top"
                    >
                        {/* Header */}
                        <div className="bg-white border-b border-gray-100 p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                                        <ShoppingBag className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div>
                                        <h2 className="font-display font-bold text-lg text-gray-900">
                                            Votre Panier
                                        </h2>
                                        {totalItems > 0 && (
                                            <p className="text-sm text-gray-500">
                                                {totalItems} {totalItems === 1 ? 'article' : 'articles'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={closeCart}
                                    className="w-11 h-11 flex items-center justify-center rounded-full bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                                    aria-label="Fermer le panier"
                                >
                                    <X className="w-5 h-5 text-gray-600" />
                                </button>
                            </div>

                            {/* Free Shipping Progress Bar */}
                            {items.length > 0 && (
                                <div className="mt-4">
                                    {hasFreeShipping ? (
                                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl">
                                            <PartyPopper className="w-5 h-5 text-green-600" />
                                            <span className="text-sm font-semibold text-green-700">
                                                🎉 Félicitations! Livraison GRATUITE!
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-gray-600 flex items-center gap-1">
                                                    <Truck className="w-4 h-4" />
                                                    Livraison gratuite
                                                </span>
                                                <span className="font-semibold text-primary-600">
                                                    Plus que {formatPrice(amountToFreeShipping)}
                                                </span>
                                            </div>
                                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${freeShippingProgress}%` }}
                                                    className="h-full bg-gradient-to-r from-primary-400 to-primary-600 rounded-full"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Cart Items */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {items.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                                        <ShoppingBag className="w-12 h-12 text-gray-300" />
                                    </div>
                                    <h3 className="font-display font-bold text-xl text-gray-900 mb-2">
                                        Votre panier est vide
                                    </h3>
                                    <p className="text-gray-500 text-sm mb-6 max-w-xs">
                                        Découvrez nos produits et profitez de la livraison gratuite!
                                    </p>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={closeCart}
                                        className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-colors"
                                    >
                                        Découvrir les produits
                                    </motion.button>
                                </div>
                            ) : (
                                items.map((item) => (
                                    <motion.div
                                        key={item.productId}
                                        layout
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, x: -100 }}
                                        className="bg-white rounded-2xl p-4 shadow-sm"
                                    >
                                        <div className="flex gap-4">
                                            {/* Product Image */}
                                            <div className="w-24 h-24 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0">
                                                <img
                                                    src={item.imageUrl || getImagePlaceholder(96, 96)}
                                                    alt={item.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>

                                            {/* Product Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 leading-tight">
                                                        {item.name}
                                                    </h4>
                                                    <button
                                                        onClick={() => removeItem(item.productId)}
                                                        className="text-gray-400 hover:text-red-500 transition-colors p-1 shrink-0"
                                                        aria-label="Supprimer"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>

                                                <p className="text-lg font-bold text-primary-600 mt-1">
                                                    {formatPrice(item.price)}
                                                </p>

                                                {/* Quantity Controls */}
                                                <div className="flex items-center justify-between mt-3">
                                                    <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
                                                        <button
                                                            onClick={() =>
                                                                updateQuantity(item.productId, item.quantity - 1)
                                                            }
                                                            className="w-9 h-9 flex items-center justify-center bg-white rounded-lg shadow-sm active:bg-gray-100 transition-colors touch-manipulation"
                                                            aria-label="Diminuer"
                                                        >
                                                            <Minus className="w-4 h-4" />
                                                        </button>
                                                        <span className="w-10 text-center font-bold text-gray-900 tabular-nums">
                                                            {item.quantity}
                                                        </span>
                                                        <button
                                                            onClick={() =>
                                                                updateQuantity(item.productId, item.quantity + 1)
                                                            }
                                                            className="w-9 h-9 flex items-center justify-center bg-white rounded-lg shadow-sm active:bg-gray-100 transition-colors touch-manipulation"
                                                            aria-label="Augmenter"
                                                        >
                                                            <Plus className="w-4 h-4" />
                                                        </button>
                                                    </div>

                                                    <span className="text-sm font-semibold text-gray-700">
                                                        = {formatPrice(item.price * item.quantity)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </div>

                        {/* Footer with Total & Checkout */}
                        {items.length > 0 && (
                            <div className="bg-white border-t border-gray-200 p-4 pb-6 space-y-4 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.1)] safe-area-bottom">
                                {/* Trust Badges */}
                                <div className="grid grid-cols-3 gap-2 pb-3 border-b border-gray-100">
                                    <div className="flex flex-col items-center gap-1 text-center">
                                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                            <CreditCard className="w-4 h-4 text-green-600" />
                                        </div>
                                        <span className="text-xs text-gray-600 font-medium">Paiement COD</span>
                                    </div>
                                    <div className="flex flex-col items-center gap-1 text-center">
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                            <Truck className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <span className="text-xs text-gray-600 font-medium">58 Wilayas</span>
                                    </div>
                                    <div className="flex flex-col items-center gap-1 text-center">
                                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                            <ShieldCheck className="w-4 h-4 text-purple-600" />
                                        </div>
                                        <span className="text-xs text-gray-600 font-medium">Garanti</span>
                                    </div>
                                </div>

                                {/* Order Summary */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600">Sous-total</span>
                                        <span className="font-semibold text-gray-900">{formatPrice(totalPrice)}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600">Livraison</span>
                                        {hasFreeShipping ? (
                                            <span className="font-semibold text-green-600 flex items-center gap-1">
                                                <Gift className="w-4 h-4" />
                                                GRATUITE
                                            </span>
                                        ) : (
                                            <span className="text-gray-500">Calculée à la commande</span>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                        <span className="font-bold text-gray-900">Total</span>
                                        <span className="font-display font-bold text-2xl text-gray-900">
                                            {formatPrice(totalPrice)}
                                        </span>
                                    </div>
                                </div>

                                {/* COD Badge - Controlled by Settings */}
                                {codEnabled && (
                                    <div className="flex items-center justify-center gap-2 py-2 bg-green-50 border border-green-200 rounded-xl">
                                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                                        <span className="text-sm font-semibold text-green-700">💵 {codLabel}</span>
                                    </div>
                                )}

                                {/* Checkout Button */}
                                <motion.button
                                    whileTap={{ scale: 0.98 }}
                                    onClick={handleCheckout}
                                    className="w-full py-4 bg-gradient-to-r from-primary-500 to-primary-600 active:from-primary-600 active:to-primary-700 text-white font-bold text-lg rounded-xl shadow-lg shadow-primary-500/30 flex items-center justify-center gap-3 transition-all touch-manipulation"
                                >
                                    <Zap className="w-5 h-5" />
                                    Commander Maintenant
                                </motion.button>

                                {/* Continue Shopping */}
                                <button
                                    onClick={closeCart}
                                    className="w-full py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                    ← Continuer mes achats
                                </button>
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
